import { memo } from "react";
import { Card, CardContent, CardFooter, CardHeader } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CalendarDays, MapPin } from "lucide-react";
import { Event } from "@/lib/types";
import { useNavigate } from "react-router-dom";
import { formatInTimeZone } from 'date-fns-tz';
import { getCurrencySymbol } from "@/lib/currencyUtils";

interface EventCardProps {
  event: Event;
  isPastEvent?: boolean;
}

export const EventCard = memo(({ event, isPastEvent = false }: EventCardProps) => {
  const navigate = useNavigate();
  const currencySymbol = getCurrencySymbol(event.currency);
  
  
  // Format the date in the event's timezone
  const formattedDate = formatInTimeZone(
    new Date(event.date),
    event.timezone || 'UTC',
    'MMMM d, yyyy h:mm aa'
  );

  const handleNavigateToEvent = () => {
    navigate(`/events/${event.seo_url}`);
  };

  // Check if event is more than 5 hours past
  const eventDate = new Date(event.date);
  const currentTime = new Date();
  const fiveHoursInMs = 5 * 60 * 60 * 1000; // 5 hours in milliseconds
  const isEventPast = currentTime.getTime() - eventDate.getTime() > fiveHoursInMs;

  // Check if event is sold out
  const isSoldOut = () => {
    const hasEarlyBird = event.early_bird_price && event.quantity_early_bird && event.quantity_early_bird > 0;
    const hasGeneralAdmission = event.price_general_admission && event.quantity_standing && event.quantity_standing > 0;
    const hasVip = event.price_vip && event.quantity_vip && event.quantity_vip > 0;
    
    return !hasEarlyBird && !hasGeneralAdmission && !hasVip;
  };

  // Calculate the cheapest ticket price including Slay fee
  const calculateTotalPrice = () => {
    const prices = [];
    
    // Add available ticket prices
    if (event.early_bird_price && event.quantity_early_bird && event.quantity_early_bird > 0) {
      prices.push(event.early_bird_price);
    }
    if (event.price_general_admission && event.quantity_standing && event.quantity_standing > 0) {
      prices.push(event.price_general_admission);
    }
    if (event.price_vip && event.quantity_vip && event.quantity_vip > 0) {
      prices.push(event.price_vip);
    }
    
    // Find the cheapest price
    const cheapestPrice = Math.min(...prices);
    
    // Calculate Slay fee
    const slayFeePercentage = event.slay_fee_percentage || 0;
    const slayFeeFixed = event.slay_fee_fixed || 0;
    const slayFeeAmount = (cheapestPrice * slayFeePercentage / 100) + slayFeeFixed;
    
    // Return total price including Slay fee
    return cheapestPrice + slayFeeAmount;
  };

  const totalPrice = calculateTotalPrice();
  const soldOut = isSoldOut();

  const handleGetTicketsClick = () => {
    navigate(`/events/${event.seo_url}`);
    // Scroll to top after navigation
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 100);
  };

  return (
    <Card className="overflow-hidden bg-gray-900 border-gray-800 hover:border-gray-700 transition-colors card-hover">
      <div className="aspect-video sm:aspect-[16/9] relative overflow-hidden">
        <img
          src={event.image_url || "/placeholder.svg"}
          alt={event.title}
          className="object-cover w-full h-full transition-transform hover:scale-105 duration-300"
          loading="lazy"
          width={640}
          height={360}
        />
      </div>
      <CardHeader 
        className="space-y-2 px-3 sm:px-6 py-3 sm:py-4 cursor-pointer hover:bg-gray-800 transition-colors touch-feedback"
        onClick={handleNavigateToEvent}
      >
        <h3 className="text-base sm:text-lg md:text-2xl font-semibold line-clamp-2 text-white">
          {event.title}
        </h3>
        {event.subtitle && (
          <p className="text-sm sm:text-base md:text-lg text-gray-300">{event.subtitle}</p>
        )}
      </CardHeader>
      <CardContent className="px-3 sm:px-6 py-2 sm:py-4">
        <div className="space-y-2 sm:space-y-3 md:space-y-4">
          <div className="flex items-center text-gray-300">
            <CalendarDays className="h-4 w-4 sm:h-5 sm:w-5 mr-2 sm:mr-3 flex-shrink-0" />
            <span className="text-xs sm:text-sm md:text-base">{formattedDate}</span>
          </div>
          <div className="flex items-center text-gray-300">
            <MapPin className="h-4 w-4 sm:h-5 sm:w-5 mr-2 sm:mr-3 flex-shrink-0" />
            <span className="text-xs sm:text-sm md:text-base">
              {event.location_url ? (
                <a 
                  href={event.location_url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="hover:text-[#9b87f5] transition-colors focus-visible-ring"
                >
                  {event.location}
                </a>
              ) : (
                event.location
              )}
            </span>
          </div>
          <p className="text-xs sm:text-sm md:text-base text-gray-300 line-clamp-3">
            {event.description}
          </p>
        </div>
      </CardContent>
      <CardFooter className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-4 px-3 sm:px-6 py-3 sm:py-6">
        <div className="space-y-1 w-full sm:w-auto">
          {soldOut && !isEventPast ? (
            <div className="bg-red-500/20 border border-red-500/50 rounded-lg p-3 text-center">
              <p className="text-red-400 font-bold text-sm sm:text-base flex items-center justify-center gap-2">
                <span className="text-lg">🔥</span>
                All Tickets Snatched. Join the Waitlist.
              </p>
            </div>
          ) : !isEventPast ? (
            <>
              <span className="text-xs sm:text-sm text-gray-400">From (inc. fees)</span>
              <p className="text-sm sm:text-base md:text-xl font-bold text-white">
                {currencySymbol}{totalPrice.toFixed(2)}
              </p>
            </>
          ) : null}
        </div>
        <Button 
          onClick={handleGetTicketsClick}
          className="w-full sm:w-auto bg-gradient-to-r from-[#9b87f5] to-[#D946EF] hover:opacity-90 text-white text-sm sm:text-base py-2 sm:py-3 touch-feedback focus-visible-ring min-h-[44px] sm:min-h-[unset]"
        >
          {soldOut && !isEventPast ? "Join Waitlist" : isPastEvent || isEventPast ? "View Event" : "Get Tickets"}
        </Button>
      </CardFooter>
    </Card>
  );
});

EventCard.displayName = "EventCard";
